<template>
  <div class="individual-information">
    <div class="contact-info-header">
      <h3>
        {{ contact.NA ? 'Department' : 'Individual' }} Information<span v-if="contact.INACTIVE" class="inactive-tag"> (Inactive)</span>
      </h3>
      <div class="contact-type-container">
        <Checkbox label="Individual" :modelValue="!contact.NA" @update:modelValue="contact.NA = false" />
        <Checkbox label="Department" :modelValue="contact.NA" @update:modelValue="contact.NA = true" />
      </div>
    </div>
    <div class="input-container">
      <div class="double-column">
        <TextBox v-if="!contact.NA" label="First Name" v-model="contact.FIRSTNAME" :invalid="showValidationErrors && !contact.FIRSTNAME && !contact.NA" />
        <TextBox v-if="!contact.NA" label="Last Name" v-model="contact.LASTNAME" :invalid="showValidationErrors && !contact.LASTNAME && !contact.NA" />
        <MultiDropdown v-if="contact.NA" label="Department" v-model="contact.DEPARTMENTS" :options="departmentOptions" class="double" :invalid="showValidationErrors && !contact.DEPARTMENTS.length && contact.NA" />
        <TextBox
          label="Company"
          :readonly="true"
          v-model="companyName"
          class="double"
          :showExtLink="!!companyName"
          @navigateToLink="navigateToCompany()"
          :showPlus="!companyName"
          @handlePlus="$emit('showLinkCompany')"
          :showUnlink="!!companyName && !contact.NA"
          @handleUnlink="$emit('handleUnlink')"
          :invalid="showValidationErrors && !contact.COMPANYID && !contact.company?.NAME && contact.NA" />
        <MultiDropdown v-if="!contact.NA" label="Title" v-model="contact.TITLES" :options="titleOptions" />
        <MultiDropdown v-if="!contact.NA" label="Department" v-model="contact.DEPARTMENTS" :options="departmentOptions" :invalid="showValidationErrors && !contact.DEPARTMENTS.length && contact.NA" />
        <div class="phone-with-ext" :class="{ double: contact.NA }">
          <TextBox label="Direct Phone" format="phone" v-model="directPhone" :invalid="(showValidationErrors && !directPhone && !mobilePhone && !contact.NA) || (showValidationErrors && contact.NA && !directPhone)" />
          <TextBox label="Ext" v-model="directPhoneExt" />
        </div>
        <TextBox v-if="!contact.NA" label="Mobile Phone" format="phone" v-model="mobilePhone" :invalid="showValidationErrors && !mobilePhone && !directPhone && !contact.NA" />
        <TextBox label="Company Phone" format="phone" :modelValue="companyMainPhone" :readonly="true" :class="{ double: contact.NA }" />
        <TextBox label="Company Fax" format="phone" :modelValue="companyFaxPhone" :readonly="true" :class="{ double: contact.NA }" />
      </div>
      <div class="double-column">
        <AddressSearch
          label="Address Line 1"
          class="double"
          v-model="contact.address.LINE1"
          :options="lookupResults"
          :showExtLink="true"
          @navigateToLink="navigateToAddress()"
          @autofillAddress="autofillAddress"
          @clearLookup="lookupResults.length = 0" />
        <TextBox label="Line 2" v-model="contact.address.LINE2" />
        <AddressSearch label="City" v-model="contact.address.CITY" :options="lookupResults" @autofillAddress="autofillAddress" @clearLookup="lookupResults.length = 0" />
        <Dropdown label="State" v-model="contact.address.STATE" :options="stateAbbreviations" :canBeEmpty="true" />
        <AddressSearch label="Zip" v-model="contact.address.POSTALCODE" :options="lookupResults" @autofillAddress="autofillAddress" @clearLookup="lookupResults.length = 0" />
        <TextBox label="Email" v-model="contactEmail" :showExtLink="contactEmail.includes('@') ? true : false" @navigateToLink="navigateToEmail()" />
        <TextBox label="Birthday" type="date" v-model="contactBirthdate" />
        <TextBox label="Company Website" :modelValue="companyWebsite" :readonly="true" :showExtLink="!!companyWebsite" @navigateToLink="navigateToWebsite()" />
        <Dropdown label="Country" v-model="contact.address.COUNTRYNAME" :options="countries" :canBeEmpty="true" />
      </div>
    </div>
  </div>
</template>

<script>
import TextBox from './inputs/TextBox.vue';
import Dropdown from './inputs/Dropdown.vue';
import MultiDropdown from './inputs/MultiDropdown.vue';
import { phoneTypes } from '../composables/usePhoneTypes.js';
import usePicklistItemsQuery from '../composables/usePicklistItemsQuery.js';
import useStatesAndCountries from '../composables/useStatesAndCountries.js';
import useAddressLookup from '../composables/useAddressLookup.js';
import { computed } from 'vue';
import AddressSearch from './inputs/AddressSearch.vue';
import Checkbox from './inputs/Checkbox.vue';

export default {
  name: 'IndividualInformation',
  components: {
    TextBox,
    Dropdown,
    MultiDropdown,
    AddressSearch,
    Checkbox,
  },

  emits: ['showLinkCompany', 'handleUnlink'],

  props: {
    contact: {
      type: Object,
      default: null,
    },
    showValidationErrors: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { stateAbbreviations, countries } = useStatesAndCountries();
    const { addressLookup, lookupResults } = useAddressLookup();

    const titleOptions = computed(() => {
      const baseOptions = usePicklistItemsQuery.pickLists.contactTitles.items.map((item) => item.NAME) || [];
      const currentValues = props.contact?.TITLES || [];

      // Find selected values that are not in the base options
      const missingValues = currentValues.filter((value) => value && !baseOptions.includes(value));

      // Add missing values to the top of the list
      return [...missingValues, ...baseOptions];
    });

    const departmentOptions = computed(() => {
      const baseOptions = usePicklistItemsQuery.pickLists.contactDepartments.items.map((item) => item.NAME) || [];
      const currentValues = props.contact?.DEPARTMENTS || [];

      // Find selected values that are not in the base options
      const missingValues = currentValues.filter((value) => value && !baseOptions.includes(value));

      // Add missing values to the top of the list
      return [...missingValues, ...baseOptions];
    });

    return {
      titleOptions,
      departmentOptions,
      stateAbbreviations,
      countries,
      addressLookup,
      lookupResults,
    };
  },
  created() {
    this.initializeContact();
  },
  watch: {
    contact: {
      handler() {
        this.initializeContact();
      },
      immediate: true,
    },
    'contact.address.LINE1'() {
      this.updateAddressLookup();
    },
    'contact.address.CITY'() {
      this.updateAddressLookup();
    },
    'contact.address.STATE'() {
      this.updateAddressLookup();
    },
    'contact.address.POSTALCODE'() {
      this.updateAddressLookup();
    },
  },
  computed: {
    companyName() {
      return this.contact?.company?.NAME || '';
    },
    companyWebsite() {
      return this.contact?.company?.WEBADDRESS || '';
    },
    directPhone: {
      get() {
        const phone = this.contact?.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.direct);
        return phone?.NUMBERDISPLAY || '';
      },
      set(value) {
        const phone = this.contact?.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.direct);
        if (phone) {
          phone.NUMBERDISPLAY = value;
        }
      },
    },
    directPhoneExt: {
      get() {
        const phone = this.contact?.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.direct);
        return phone?.EXT || '';
      },
      set(value) {
        const phone = this.contact?.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.direct);
        if (phone) {
          phone.EXT = value;
        }
      },
    },
    mobilePhone: {
      get() {
        const phone = this.contact?.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.mobile);
        return phone?.NUMBERDISPLAY || '';
      },
      set(value) {
        const phone = this.contact?.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.mobile);
        if (phone) {
          phone.NUMBERDISPLAY = value;
        }
      },
    },
    companyMainPhone() {
      const phone = this.contact?.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.main);
      return phone?.NUMBERDISPLAY || '';
    },
    companyFaxPhone() {
      const phone = this.contact?.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.fax);
      return phone?.NUMBERDISPLAY || '';
    },
    contactEmail: {
      get() {
        return this.contact?.email?.ADDRESS || '';
      },
      set(value) {
        if (!this.contact.email) {
          this.contact.email = { ADDRESS: '' };
        }
        this.contact.email.ADDRESS = value;
      },
    },
    contactBirthdate: {
      get() {
        if (!this.contact?.BIRTHDATE) return '';

        // First try to parse as a date directly
        try {
          const date = new Date(this.contact.BIRTHDATE);
          if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
          }
        } catch (e) {
          // Continue to the next approach if this fails
        }

        // If direct parsing fails, try parsing as epoch timestamp
        const epochTimestamp = parseInt(this.contact.BIRTHDATE);
        if (!isNaN(epochTimestamp)) {
          const date = new Date(epochTimestamp);
          return date.toISOString().split('T')[0];
        }

        // If all parsing attempts fail, return empty string
        return '';
      },
      set(value) {
        if (!value) {
          this.contact.BIRTHDATE = null;
          return;
        }
        const date = new Date(value);
        this.contact.BIRTHDATE = value;
      },
    },
  },
  methods: {
    initializeContact() {
      if (this.contact) {
        // Ensure address object exists
        if (!this.contact.address) {
          this.contact.address = {};
        }

        // Ensure phones array exists
        if (!this.contact.phones) {
          this.contact.phones = [];
        }

        // Ensure required phone types exist
        const requiredPhoneTypes = [phoneTypes.contact.direct, phoneTypes.contact.mobile];

        requiredPhoneTypes.forEach((typeId) => {
          const existingPhone = this.contact.phones.find((phone) => phone.TYPEID === typeId);
          if (!existingPhone) {
            this.contact.phones.push({
              NUMBERDISPLAY: '',
              TYPEID: typeId,
              __typename: 'TBL_PHONE',
            });
          }
        });

        // Ensure company phones array exists for read-only display
        if (this.contact.company && !this.contact.company.phones) {
          this.contact.company.phones = [];
        }

        // Ensure arrays exist for MultiDropdown fields
        if (!this.contact.TITLES) {
          this.contact.TITLES = [];
        }
        if (!this.contact.DEPARTMENTS) {
          this.contact.DEPARTMENTS = [];
        }
      }
    },
    autofillAddress(option) {
      if (option.address) this.contact.address.LINE1 = option.address;
      if (option.city) this.contact.address.CITY = option.city;
      if (option.state) this.contact.address.STATE = option.state;
      if (option.zip) this.contact.address.POSTALCODE = option.zip;
    },
    updateAddressLookup() {
      if (this.contact?.address?.LINE1 && this.contact?.address?.CITY && this.contact?.address?.STATE && this.contact?.address?.POSTALCODE) return;
      this.addressLookup(this.contact?.address?.LINE1, this.contact?.address?.CITY, this.contact?.address?.STATE, this.contact?.address?.POSTALCODE);
    },
    navigateToEmail() {
      window.open(`mailto:${this.contactEmail}`, '_blank');
    },
    navigateToAddress() {
      window.open(
        `https://www.google.com/maps/search/${encodeURIComponent(this.contact.address.LINE1)} ${encodeURIComponent(this.contact.address.LINE2)} ${encodeURIComponent(this.contact.address.CITY)} ${encodeURIComponent(
          this.contact.address.STATE
        )} ${encodeURIComponent(this.contact.address.POSTALCODE)}`,
        '_blank'
      );
    },
    navigateToWebsite() {
      window.open('https://' + this.companyWebsite, '_blank');
    },
    navigateToCompany() {
      this.$router.push({ name: 'companies', params: { companyid: this.contact.company.COMPANYID } });
    },
  },
};
</script>

<style scoped lang="scss">
.individual-information {
  flex: 5 0 45%;
  max-width: 80rem;

  .input-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1.5rem;

    .double-column {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1rem;
      flex: 1 0 48%;
      min-width: 20rem;

      & > * {
        flex: 1 0 48%;
      }

      .double {
        flex: 1 0 100%;
      }

      .phone-with-ext {
        display: flex;
        flex-direction: row;
        gap: 1rem;

        & > :first-child {
          flex: 1;
        }

        & > :last-child {
          width: 4rem;
        }
      }
    }
  }

  .contact-info-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .inactive-tag {
      color: $textColor38;
    }

    .contact-type-container {
      display: flex;
      flex-direction: row;
      gap: 1rem;
    }
  }
}
</style>
